<template>
  <div class="navbar">
    <hamburger id="hamburger-container" :is-active="sidebar.opened" class="hamburger-container" @toggleClick="toggleSideBar" />

    <breadcrumb id="breadcrumb-container" class="breadcrumb-container" />

    <div class="right-menu">

      <TaskList />

      <template v-if="device!=='mobile'">
        <LangSelect id="lang-select" class="right-menu-item" />
      </template>

      <el-dropdown class="avatar-container right-menu-item hover-effect" trigger="click">
        <div class="avatar-wrapper">
          <img src="@/assets/img/head.png" alt="" class="user-avatar">
        </div>
        <el-dropdown-menu slot="dropdown">
          <div class="user-info">
            <div>{{ name }}</div>
          </div>
          <el-dropdown-item @click.native="openDialog">
            <div>{{ $tr('navbar.changePassword', 'Change Password') }}</div>
          </el-dropdown-item>
          <el-dropdown-item divided @click.native="logout">
            <span style="display:block;">{{ $tr('navbar.logout', 'Log Out') }}</span>
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <el-dialog
      :title="$tr('navbar.changePassword', 'Change Password')"
      :visible.sync="dialogVisible"
      :close-on-click-modal="false"
      :modal="true"
      append-to-body
      width="30%"
    >
      <el-form
        ref="passwordForm"
        :model="passwordForm"
        :rules="loginRules"
        class="password-form"
        autocomplete="on"
        label-position="top"
      >
        <el-form-item :label="$tr('navbar.oldPassword', 'Old Password')" prop="oldPassword">
          <el-input
            :key="passwordType"
            ref="oldPassword"
            v-model="passwordForm.oldPassword"
            :type="passwordType"
            :placeholder="$tr('navbar.pleaseEnterOldPassword', 'please enter old password')"
            name="oldPassword"
            tabindex="2"
            size="small"
            autocomplete="on"
            @keyup.native="checkCapslock"
            @blur="capsTooltip = false"
          />
          <span class="show-pwd" @click="showPwd">
            <svg-icon :icon-class="passwordType === 'password' ? 'eye' : 'eye-open'" />
          </span>
        </el-form-item>

        <el-form-item :label="$tr('navbar.newPassword', 'New Password')" prop="newPassword">
          <el-input
            :key="passwordType2"
            ref="newPassword"
            v-model="passwordForm.newPassword"
            :type="passwordType2"
            :placeholder="$tr('navbar.pleaseEnterNewPassword', 'please enter new password')"
            name="newPassword"
            tabindex="2"
            autocomplete="on"
            size="small"
            @keyup.native="checkCapslock"
            @blur="capsTooltip = false"
          />
          <span class="show-pwd" @click="showPwd2">
            <svg-icon :icon-class="passwordType2 === 'password' ? 'eye' : 'eye-open'" />
          </span>
        </el-form-item>

        <el-form-item :label="$tr('navbar.confirmNewPassword', 'Confirm New Password')" prop="confirmPassword">
          <el-input
            :key="passwordType3"
            ref="confirmPassword"
            v-model="passwordForm.confirmPassword"
            :type="passwordType3"
            :placeholder="$tr('navbar.pleaseConfirmNewPassword', 'please confirm new password')"
            name="confirmPassword"
            tabindex="2"
            autocomplete="on"
            size="small"
            @keyup.native="checkCapslock"
            @blur="capsTooltip = false"
          />
          <span class="show-pwd" @click="showPwd3">
            <svg-icon :icon-class="passwordType3 === 'password' ? 'eye' : 'eye-open'" />
          </span>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button size="mini" type="primary" @click="changePassword">{{ $tr('navbar.save', 'Save') }}</el-button>
        <el-button size="mini" @click="closeDialog()">{{ $tr('navbar.cancel', 'Cancel') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Breadcrumb from '@/components/Breadcrumb'
import Hamburger from '@/components/Hamburger'
import LangSelect from '@/components/LangSelect'
import TaskList from '@/components/TaskList'
import { processChangePasswordData } from '@/utils/encrypt'

export default {
  components: {
    Breadcrumb,
    Hamburger,
    LangSelect,
    TaskList
  },
  data() {
    const validatePassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$tr('navbar.passwordRequired', 'Please enter password')))
      } else if (value.length < 6) {
        callback(new Error(this.$tr('navbar.passwordTooShort', 'The password can not be less than 6 digits')))
      } else {
        callback()
      }
    }
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$tr('navbar.confirmPasswordRequired', 'Please confirm password')))
      } else if (value !== this.passwordForm.newPassword) {
        callback(new Error(this.$tr('navbar.passwordsMismatch', 'The two passwords do not match')))
      } else {
        callback()
      }
    }
    return {
      passwordType: 'password',
      passwordType2: 'password',
      passwordType3: 'password',
      dialogVisible: false,
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      loginRules: {
        oldPassword: [{ required: true, trigger: 'blur', validator: validatePassword }],
        newPassword: [{ required: true, trigger: 'blur', validator: this.validateNewPassword }],
        confirmPassword: [{ required: true, trigger: 'blur', validator: validateConfirmPassword }]
      },
      capsTooltip: false
    }
  },
  computed: {
    ...mapGetters([
      'sidebar',
      'avatar',
      'device',
      'name'
    ])
  },
  methods: {
    closeDialog() {
      this.$refs.passwordForm.resetFields()
      this.dialogVisible = false
    },
    changePassword() {
      this.$refs.passwordForm.validate(valid => {
        if (valid) {
          const encryptedData = processChangePasswordData(this.passwordForm)
          this.$store.dispatch('user/resetPassword', encryptedData).then(() => {
            this.dialogVisible = false
            this.$message({
              message: this.$tr('navbar.changePasswordSuccess', 'Change password successfully'),
              type: 'success'
            })
          }).catch(() => {
            this.$message({
              message: this.$tr('navbar.changePasswordFailed', 'Change password failed'),
              type: 'error'
            })
          })
        } else {
          return false
        }
      })
    },
    showPwd() {
      if (this.passwordType === 'password') {
        this.passwordType = ''
      } else {
        this.passwordType = 'password'
      }
      this.$nextTick(() => {
        this.$refs.oldPassword.focus()
      })
    },
    showPwd2() {
      if (this.passwordType2 === 'password') {
        this.passwordType2 = ''
      } else {
        this.passwordType2 = 'password'
      }
      this.$nextTick(() => {
        this.$refs.newPassword.focus()
      })
    },
    showPwd3() {
      if (this.passwordType3 === 'password') {
        this.passwordType3 = ''
      } else {
        this.passwordType3 = 'password'
      }
      this.$nextTick(() => {
        this.$refs.confirmPassword.focus()
      })
    },
    validateNewPassword(rule, value, callback) {
      if (!value) {
        callback(new Error(this.$tr('navbar.newPasswordRequired', 'please enter new password')))
      } else if (value.length < 6) {
        callback(new Error(this.$tr('navbar.passwordTooShort', 'The password can not be less than 6 digits')))
      } else {
        callback()
      }
    },
    checkCapslock(e) {
      const { key } = e
      this.capsTooltip = key && key.length === 1 && (key >= 'A' && key <= 'Z')
    },
    openDialog() {
      this.dialogVisible = true
    },
    toggleSideBar() {
      this.$store.dispatch('app/toggleSideBar')
    },
    async logout() {
      await this.$store.dispatch('user/logout')
      this.$router.push(`/login`)
    }
  }
}
</script>

<style lang="scss" scoped>
$dark_gray:#889aa4;
.user-info {
  padding: 10px 20px;
  font-size: 15px;
  border-bottom: 1px solid rgba(0,0,0,.1);
  div {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }
}
.show-pwd {
  position: absolute;
  right: 10px;
  top: 2px;
  font-size: 16px;
  color: $dark_gray;
  cursor: pointer;
  user-select: none;
}

::v-deep .password-form {
  font-size: 13px;
  .el-form-item__label {
    line-height: 25px;
    height: 25px;
  }
}

.navbar {
  height: 50px;
  overflow: hidden;
  position: relative;
  background: #2a303e;
  box-shadow: 0 1px 4px rgba(0,21,41,.08);

  .hamburger-container {
    line-height: 46px;
    height: 100%;
    float: left;
    cursor: pointer;
    transition: background .3s;
    -webkit-tap-highlight-color:transparent;

    &:hover {
      background: rgba(0, 0, 0, .025)
    }
  }

  .breadcrumb-container {
    float: left;
  }

  .errLog-container {
    display: inline-block;
    vertical-align: top;
  }

  .right-menu {
    float: right;
    height: 100%;
    line-height: 50px;

    &:focus {
      outline: none;
    }

    .right-menu-item {
      display: inline-block;
      padding: 0 8px;
      height: 100%;
      font-size: 18px;
      color: #5a5e66;
      vertical-align: text-bottom;

      &.hover-effect {
        cursor: pointer;
        transition: background .3s;

        &:hover {
          background: rgba(0, 0, 0, .025)
        }
      }
    }

    .avatar-container {
      margin-right: 30px;

      .avatar-wrapper {
        margin-top: 5px;
        position: relative;

        .user-avatar {
          cursor: pointer;
          width: 35px;
          height: 35px;
          margin-top: 3px;
          border-radius: 50%;
        }

        .el-icon-caret-bottom {
          cursor: pointer;
          position: absolute;
          right: -20px;
          top: 25px;
          font-size: 12px;
        }
      }
    }
  }
}
</style>
